import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import PageRenderer from '../../components/common/PageRenderer';

function generateCaptcha() {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { question: `${a} + ${b}`, answer: String(a + b) };
}

function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [captcha, setCaptcha] = useState(() => generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captchaInput.trim() !== captcha.answer) {
      toast.error('Incorrect answer. Please solve the math question.');
      setCaptcha(generateCaptcha());
      setCaptchaInput('');
      return;
    }
    setSending(true);
    try {
      await api.post('/messages/submit/', {
        ...formData,
        source: 'contact',
      });
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setCaptcha(generateCaptcha());
      setCaptchaInput('');
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="lg:max-w-3xl">
          <div className="bg-[#F8FAFB] rounded-2xl p-8 md:p-10 border border-gray-100">
            <h2 className="text-2xl font-bold text-[#111827] mb-2 section-heading" style={{ fontFamily: 'Inter, sans-serif' }}>
              Send us a Message
            </h2>
            <p className="text-gray-500 text-sm mb-8 mt-4">Fill out the form below and we'll respond within 24–48 hours.</p>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name</label>
                  <input type="text" required value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                  <input type="email" required value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Subject</label>
                <input type="text" required value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="What is this regarding?"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message</label>
                <textarea rows={5} required value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Type your message here..."
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition text-sm resize-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Security Check: What is {captcha.question}?</label>
                <input type="text" required value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  placeholder="Enter your answer"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition text-sm" />
              </div>
              <button type="submit" disabled={sending}
                className="w-full sm:w-auto bg-[#0B5D1E] hover:bg-[#094A18] disabled:opacity-60 text-white px-8 py-3.5 rounded-lg text-sm font-bold tracking-wide transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2">
                {sending ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</>
                ) : (
                  <>Send Message <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg></>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function DefaultContent() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B5D1E]" style={{ backgroundImage: 'linear-gradient(135deg, #0B5D1E 0%, #094A18 100%)' }}>
        <div className="relative" style={{ paddingTop: 80, paddingBottom: 80, zIndex: 1 }}>
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, color: '#fff', marginBottom: 16, fontFamily: "'Playfair Display',serif" }}>
              Contact Us
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Have a question or need assistance? We'd love to hear from you
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />

      {/* Contact Info */}
      <section className="py-16 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: (<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>), title: 'Office Address', desc: 'Federal Capital Territory, Abuja, Nigeria' },
              { icon: (<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>), title: 'Email', desc: 'info@nisfctbranch.org' },
              { icon: (<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>), title: 'Phone', desc: 'Contact the branch secretariat' },
            ].map((c) => (
              <div key={c.title} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm text-center">
                <div className="w-12 h-12 rounded-lg bg-[#0B5D1E]/10 text-[#0B5D1E] flex items-center justify-center mx-auto mb-4">{c.icon}</div>
                <h3 className="text-base font-bold text-gray-900 mb-1">{c.title}</h3>
                <p className="text-gray-500 text-sm">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default function Contact() {
  return (
    <PageRenderer
      pageKey="contact"
      insertAfter={{
        'Contact Hero': () => <ContactForm />,
      }}
      fallback={<DefaultContent />}
    />
  );
}
